class VersionManager {
  constructor() {
    this.checkInterval = null;
    this.isChecking = false;
    this.currentVersion = null;
    this.updateCallback = null;
  }

  async getCurrentVersion() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      return {
        version: data.version,
        buildNumber: data.buildNumber,
        buildDate: data.buildDate
      };
    } catch (error) {
      console.log('⚠️ Version check failed:', error.message);
      return null;
    }
  }

  clearCaches() {
    try {
      // Save user data
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      const rememberMe = localStorage.getItem('rememberMe');
      
      // Clear everything
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore user data
      if (user) localStorage.setItem('user', user);
      if (token) localStorage.setItem('token', token);
      if (isAuthenticated) localStorage.setItem('isAuthenticated', isAuthenticated);
      if (rememberMe) localStorage.setItem('rememberMe', rememberMe);
      
      // Clear service worker caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        }).catch(console.error);
      }
      
      console.log('✅ Caches cleared successfully');
      return true;
    } catch (error) {
      console.error('Failed to clear caches:', error);
      return false;
    }
  }

  showNotification(message) {
    // Remove existing notification
    const existing = document.getElementById('app-update-notification');
    if (existing) existing.remove();
    
    const notification = document.createElement('div');
    notification.id = 'app-update-notification';
    notification.innerHTML = `
      <div style="
        position: fixed;
        bottom: 24px;
        right: 24px;
        background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
        color: white;
        padding: 14px 20px;
        border-radius: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        font-weight: 500;
        z-index: 999999;
        box-shadow: 0 4px 20px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        gap: 12px;
        backdrop-filter: blur(10px);
        animation: slideUpFade 0.3s ease;
        cursor: pointer;
        max-width: 350px;
      ">
        <div style="
          width: 20px;
          height: 20px;
          border: 2px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        "></div>
        <div style="flex: 1;">
          <strong style="display: block; margin-bottom: 4px;">${message.title || 'Update Available'}</strong>
          <span style="font-size: 12px; opacity: 0.9;">${message.body || 'Refreshing to get the latest version...'}</span>
        </div>
      </div>
      <style>
        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      </style>
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (notification && notification.remove) {
        notification.remove();
      }
    }, 3000);
  }

  async checkForUpdates() {
    if (this.isChecking) return;
    this.isChecking = true;
    
    try {
      const serverVersion = await this.getCurrentVersion();
      if (!serverVersion) return;
      
      const storedVersion = localStorage.getItem('app_version');
      const storedBuildNumber = localStorage.getItem('app_build_number');
      
      // Check if version changed
      const isNewVersion = !storedVersion || 
                          serverVersion.version !== storedVersion ||
                          (serverVersion.buildNumber && serverVersion.buildNumber.toString() !== storedBuildNumber);
      
      if (isNewVersion) {
        console.log('🔄 New version detected!', {
          old: storedVersion,
          new: serverVersion.version,
          build: serverVersion.buildNumber
        });
        
        // Clear caches
        this.clearCaches();
        
        // Store new version info
        localStorage.setItem('app_version', serverVersion.version);
        localStorage.setItem('app_build_number', serverVersion.buildNumber?.toString() || '');
        localStorage.setItem('app_version_date', serverVersion.buildDate);
        
        // Show notification
        this.showNotification({
          title: `✨ New Version ${serverVersion.version}`,
          body: 'Updating to the latest version...'
        });
        
        // Call update callback if set
        if (this.updateCallback) {
          this.updateCallback(serverVersion);
        }
        
        // Reload after delay
        setTimeout(() => {
          window.location.reload(true);
        }, 1500);
        
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.log('Version check error:', error.message);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  // Manual check (can be called from console for testing)
  manualCheck() {
    console.log('🔍 Manual version check triggered');
    return this.checkForUpdates();
  }

  start(intervalMs = 60000, callback = null) {
    this.updateCallback = callback;
    
    // Wait for page to fully load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.checkForUpdates(), 3000);
      });
    } else {
      setTimeout(() => this.checkForUpdates(), 3000);
    }
    
    // Start periodic checking
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, intervalMs);
    
    console.log('✅ Version manager started (checking every', intervalMs / 1000, 'seconds)');
  }

  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    console.log('⏹️ Version manager stopped');
  }
}

// Create and export singleton instance
const versionManager = new VersionManager();
export default versionManager;